import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const CARDS = [
  {
    id: 'unmarkedAttendance',
    title: 'Unmarked Attendance — Today',
    description: "Today's class sessions that have ended without attendance being recorded.",
    href: '/admin/notifications/unmarked-attendance',
    count: 0,
    accent: 'red',
  },
  {
    id: 'staleIssues',
    title: 'Overdue Student Issues',
    description: 'Issues open for more than 3 days or in-progress for more than 7 days, grouped by course.',
    href: '/admin/notifications/stale-issues',
    count: 0,
    accent: 'amber',
  },
  {
    id: 'lowHoursSchedules',
    title: 'Batches with Low Scheduled Hours (Today)',
    description: 'Ongoing batches of key courses that are scheduled today with less than 3 hours of total lecture time.',
    href: `/admin/notifications/low-hours-schedules?date=${new Date().toISOString().slice(0, 10)}`,
    count: 0,
  },
  {
    id: 'batchesWithoutStudents',
    title: 'Batches Without Students',
    description: 'Active batches that currently have no enrolled students.',
    href: '/admin/notifications/batches-without-students',
    count: 0,
  },
  {
    id: 'studentsWithoutImages',
    title: 'Students Without Profile Images',
    description: 'Enrolled students who do not yet have a profile photograph.',
    href: '/admin/notifications/students-without-images',
    count: 0,
  },
  {
    id: 'eligiblePathwayVerification',
    title: 'Batches Eligible for Pathway Verification',
    description: 'Batches that meet the criteria and are pending pathway verification.',
    href: '/admin/notifications/pathway-verification-eligible-batches',
    count: 0,
  },
  {
    id: 'nonGraduatedPastStudents',
    title: 'Past Students Not Yet Graduated',
    description: 'Students who have completed delivery but are not marked as graduated.',
    href: '/admin/notifications/past-students-not-graduated',
    count: 0,
  },
  {
    id: 'delayedUniversityRegistrations',
    title: 'Students with Delayed University Registration',
    description: 'Students whose university registration is overdue or pending beyond the expected date.',
    href: '/admin/notifications/delayed-university-registrations',
    count: 0,
  },
];

export default function Notifications() {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadCounts = async () => {
      setLoading(true);
      setError('');

      try {
        const todayStr = new Date().toISOString().slice(0, 10);
        const [
          unmarkedRes,
          staleRes,
          lowHoursRes,
          batchesRes,
          studentsNoImgRes,
          pathwayRes,
          pastRes,
          delayedRes,
        ] = await Promise.allSettled([
          axiosInstance.get('/admin/attendance/unmarked-today'),
          axiosInstance.get('/tickets/stale'),
          axiosInstance.get('/admin/timetable/low-hours-batches', { params: { date: todayStr } }),
          axiosInstance.get('/admin/batches/without-students'),
          axiosInstance.get('/admin/students', {
            params: {
              hasImage: 'false',
              deliveryStatus: 'active',
              page: 1,
              perPage: 1,
            },
          }),
          axiosInstance.get('/admin/registrations/pathway-verification-eligible'),
          axiosInstance.get('/admin/registrations/past-students-not-graduated'),
          axiosInstance.get('/admin/registrations/delayed-university-registrations'),
        ]);

        if (cancelled) return;

        const nextCounts = {};

        if (unmarkedRes.status === 'fulfilled') {
          const list = Array.isArray(unmarkedRes.value.data) ? unmarkedRes.value.data : [];
          nextCounts.unmarkedAttendance = list.length;
        }

        if (staleRes.status === 'fulfilled') {
          const data = staleRes.value.data || {};
          const open = Array.isArray(data.overdueOpen) ? data.overdueOpen.length : 0;
          const inProg = Array.isArray(data.overdueInProgress) ? data.overdueInProgress.length : 0;
          nextCounts.staleIssues = open + inProg;
        }

        if (lowHoursRes.status === 'fulfilled') {
          const data = lowHoursRes.value.data || {};
          const list = Array.isArray(data.batches) ? data.batches : [];
          nextCounts.lowHoursSchedules = list.length;
        }

        if (batchesRes.status === 'fulfilled') {
          const data = batchesRes.value.data || {};
          const list = Array.isArray(data.batches) ? data.batches : [];
          nextCounts.batchesWithoutStudents = list.length;
        }

        if (studentsNoImgRes.status === 'fulfilled') {
          const data = studentsNoImgRes.value.data || {};
          const total = data.pagination?.total ?? (Array.isArray(data.students) ? data.students.length : 0);
          nextCounts.studentsWithoutImages = total;
        }

        if (pathwayRes.status === 'fulfilled') {
          const data = pathwayRes.value.data || {};
          const list = Array.isArray(data.students) ? data.students : [];
          nextCounts.eligiblePathwayVerification = list.length;
        }

        if (pastRes.status === 'fulfilled') {
          const data = pastRes.value.data || {};
          const list = Array.isArray(data.students) ? data.students : [];
          nextCounts.nonGraduatedPastStudents = list.length;
        }

        if (delayedRes.status === 'fulfilled') {
          const data = delayedRes.value.data || {};
          const list = Array.isArray(data.students) ? data.students : [];
          nextCounts.delayedUniversityRegistrations = list.length;
        }

        setCounts(nextCounts);

        const hasAnyRejected = [
          unmarkedRes,
          staleRes,
          lowHoursRes,
          batchesRes,
          studentsNoImgRes,
          pathwayRes,
          pastRes,
          delayedRes,
        ].some((res) => res.status === 'rejected');

        if (hasAnyRejected) {
          console.warn('Some notification counts failed to load');
        }
      } catch (err) {
        console.error('Failed to load notification summary counts', err);
        if (!cancelled) {
          setError('Failed to load notification summaries. Counts may be incomplete.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCounts();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <DashboardLayout title="Notifications">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Notifications</h1>
          <p className="mt-1 text-sm text-gray-600">
            Overview of key academic and registration alerts that require administrative attention.
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-2 text-xs text-yellow-800">
            {error}
          </div>
        )}

        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {CARDS.map((card) => {
            const isAmber = card.accent === 'amber';
            const isRed = card.accent === 'red';
            const countVal = loading && counts[card.id] == null ? '…' : counts[card.id] ?? card.count;
            return (
              <div
                key={card.id}
                className="relative flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">{card.title}</h2>
                    <p className="mt-1 text-xs text-gray-500">{card.description}</p>
                  </div>
                  <div className={`inline-flex items-center justify-center rounded-full px-3 py-1 ${isRed ? 'bg-red-50' : isAmber ? 'bg-amber-50' : 'bg-indigo-50'}`}>
                    <span className={`text-xs font-medium uppercase tracking-wide ${isRed ? 'text-red-700' : isAmber ? 'text-amber-700' : 'text-indigo-700'}`}>
                      {countVal}
                    </span>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <a
                    href={card.href}
                    target="_blank"
                    rel="noreferrer"
                    className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-medium shadow-sm transition ${
                      isRed
                        ? 'border-red-200 bg-white text-red-700 hover:border-red-400 hover:text-red-800'
                        : isAmber
                        ? 'border-amber-200 bg-white text-amber-700 hover:border-amber-400 hover:text-amber-800'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-indigo-400 hover:text-indigo-700'
                    }`}
                  >
                    View details
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
